import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { THEME, getStatusLabel } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Badge from '../ui/Badge';
import RatingModal from './RatingModal';

const GameDetailModal = ({ game, currentUser, squadMembers = [], onClose, onUpdate }) => {
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [myTags, setMyTags] = useState([]);
  const [gameTags, setGameTags] = useState(game.tags || []);
  const messagesEndRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isQueued, setIsQueued] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [isEditingRating, setIsEditingRating] = useState(false);
  const [editRating, setEditRating] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  const [hltbData, setHltbData] = useState(null);
  const [hltbLoading, setHltbLoading] = useState(false);
  const [achievementsData, setAchievementsData] = useState(null);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementsExpanded, setAchievementsExpanded] = useState(false);
  const [igdbData, setIgdbData] = useState(null);
  const [igdbLoading, setIgdbLoading] = useState(false);
  const [igdbExpanded, setIgdbExpanded] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [hostingRemotePlay, setHostingRemotePlay] = useState(false);
  const [remotePlayToast, setRemotePlayToast] = useState(null);

  const handleHostRemotePlay = async () => {
    setHostingRemotePlay(true);
    try {
      const result = await api.post(`/api/games/${game.id}/host-remote-play`, {});
      if (result.success) {
        // Launch the game
        window.location.href = result.steam_url;
        // Show instructions
        setRemotePlayToast({
          message: result.notified_count > 0
            ? `Notified ${result.notified_count} squad member${result.notified_count > 1 ? 's' : ''}! Opening ${game.name}...`
            : `Opening ${game.name}...`,
          instructions: result.instructions
        });
        // Clear toast after 8 seconds
        setTimeout(() => setRemotePlayToast(null), 8000);
      }
    } catch (err) {
      alert('Failed to host Remote Play: ' + (err.message || 'Unknown error'));
    } finally {
      setHostingRemotePlay(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get(`/api/games/${game.id}/sessions`).then(setSessions),
      api.get(`/api/games/${game.id}/messages`).then(setMessages),
      api.get('/api/tags').then(setMyTags),
      api.get('/api/pins').then(pins => setIsPinned(pins.some(p => p.game_id === game.id))).catch(() => {}),
      api.get('/api/queue').then(q => setIsQueued(q.some(i => i.game_id === game.id))).catch(() => {})
    ]).finally(() => setLoading(false));
    // Load HLTB data
    setHltbLoading(true);
    api.get(`/api/games/${game.id}/hltb`).then(setHltbData).catch(() => {}).finally(() => setHltbLoading(false));
    // Load achievements data
    setAchievementsLoading(true);
    api.get(`/api/games/${game.id}/achievements`).then(setAchievementsData).catch(() => {}).finally(() => setAchievementsLoading(false));
    // Load IGDB data (if game has steam_appid)
    if (game.steam_appid) {
      setIgdbLoading(true);
      api.get(`/api/integrations/igdb/game/${game.steam_appid}`).then(data => {
        if (data.available && data.found) setIgdbData(data.game);
      }).catch(() => {}).finally(() => setIgdbLoading(false));
    }
    // Load price data
    if (game.steam_appid) {
      api.get(`/api/integrations/prices/${game.steam_appid}`).then(data => {
        if (data.available && data.found) setPriceData(data);
      }).catch(() => {});
    }
  }, [game.id, game.steam_appid]);

  const handleTogglePin = async () => {
    setPinning(true);
    try {
      if (isPinned) {
        await api.delete(`/api/pins/${game.id}`);
      } else {
        await api.post(`/api/pins/${game.id}`);
      }
      setIsPinned(!isPinned);
    } catch (err) { alert(err.message); }
    finally { setPinning(false); }
  };

  const handleToggleQueue = async () => {
    setQueuing(true);
    try {
      if (isQueued) {
        await api.delete(`/api/queue/${game.id}`);
      } else {
        await api.post(`/api/queue/${game.id}`);
      }
      setIsQueued(!isQueued);
    } catch (err) { alert(err.message); }
    finally { setQueuing(false); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const msg = await api.post(`/api/games/${game.id}/messages`, { message: newMessage });
      setMessages(prev => [...prev, msg]);
      setNewMessage('');
      // Scroll to new message after sending
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSendingMessage(false); }
  };

  const handleToggleRecommend = async () => {
    setRecommending(true);
    try {
      await api.put(`/api/games/${game.id}/recommend`);
      await onUpdate();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setRecommending(false); }
  };

  const handleAddTag = async () => {
    if (!tagInput.trim()) return;
    setAddingTag(true);
    try {
      const result = await api.post(`/api/games/${game.id}/tags`, { tags: [tagInput.trim()] });
      setGameTags(result.game.tags || []);
      setTagInput('');
      // Refresh my tags list
      const tags = await api.get('/api/tags');
      setMyTags(tags);
      onUpdate();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setAddingTag(false); }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      const result = await api.delete(`/api/games/${game.id}/tags/${tagId}`);
      setGameTags(result.game.tags || []);
      onUpdate();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  // Use dynamic user_data from API when available
  const myUserData = game.user_data?.[currentUser?.username] || {};
  // Filter to only show squad members (not all users who have game data)
  const squadMemberUsernames = new Set(squadMembers.map(m => m.username));
  const otherUsers = Object.entries(game.user_data || {})
    .filter(([username]) => username !== currentUser?.username && squadMemberUsernames.has(username));
  const otherUserData = otherUsers[0]?.[1] || {};

  const handleStartEditRating = () => {
    const myRating = myUserData.rating ?? (currentUser?.username === 'omega' ? game.omega_rating : game.supe_rating);
    const myNotes = myUserData.notes ?? (currentUser?.username === 'omega' ? game.omega_notes : game.supe_notes);
    setEditRating(myRating);
    setEditNotes(myNotes || '');
    setIsEditingRating(true);
  };

  const handleSaveRating = async () => {
    setSavingRating(true);
    try {
      await api.put(`/api/games/${game.id}/rating`, { rating: editRating, notes: editNotes || null });
      setIsEditingRating(false);
      await onUpdate();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSavingRating(false); }
  };

  const formatPlaytime = (minutes) => { if (!minutes) return '0h'; const hours = Math.floor(minutes / 60); const mins = minutes % 60; return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`; };
  const iRecommended = myUserData.recommended ?? (currentUser?.username === 'omega' ? game.recommended_by_omega : game.recommended_by_supe);
  // Get ALL squad members who recommended this game
  const recommendedByOthers = otherUsers.filter(([, ud]) => ud.recommended).map(([username, ud]) => ({ username, displayName: ud.display_name || username, color: ud.color }));

  // Collapsible section states
  const [sectionsOpen, setSectionsOpen] = useState({
    hltb: true,
    achievements: false,
    sessions: false,
    chat: false,
  });
  const toggleSection = (key) => setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));

  // Section header component for collapsible sections
  const SectionHeader = ({ icon, title, count, sectionKey, color = THEME.textMuted }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', background: THEME.bg, border: 'none', borderRadius: sectionsOpen[sectionKey] ? '12px 12px 0 0' : '12px',
        cursor: 'pointer', transition: 'all 0.15s'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <span style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>{title}</span>
        {count !== undefined && <span style={{ color, fontSize: '12px', fontWeight: '500' }}>({count})</span>}
      </div>
      <span style={{ color: THEME.textMuted, fontSize: '14px', transform: sectionsOpen[sectionKey] ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(5,5,10,0.88)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 1000,
        overflow: 'auto',
        padding: '20px 10px',
        backdropFilter: 'blur(32px) saturate(180%)',
        WebkitBackdropFilter: 'blur(32px) saturate(180%)'
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`,
          borderRadius: '28px',
          width: '100%',
          maxWidth: '640px',
          marginTop: '20px',
          marginBottom: '20px',
          border: `1px solid ${THEME.glassBorder}`,
          boxShadow: THEME.modalShadow,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Hero Header */}
        <div style={{ position: 'relative', height: '200px', flexShrink: 0 }}>
          <img src={game.header_image} alt={game.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(18,18,26,1) 0%, rgba(18,18,26,0.6) 40%, transparent 100%)' }} />
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1, background: 'rgba(0,0,0,0.8)' }}
            whileTap={{ scale: 0.95 }}
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              background: 'rgba(0,0,0,0.5)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '50%',
              width: '42px',
              height: '42px',
              fontSize: '22px',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
          >&times;</motion.button>
          {/* Title overlay on image */}
          <div style={{ position: 'absolute', bottom: '16px', left: '20px', right: '20px' }}>
            <h2 style={{ margin: '0 0 6px', color: '#fff', fontSize: '22px', fontWeight: '700', textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>{game.name}</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', color: '#fff', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '500' }}>{game.multiplayer_type}</span>
              {game.metacritic_score && (
                <span style={{ background: game.metacritic_score >= 75 ? THEME.success : game.metacritic_score >= 50 ? THEME.warning : THEME.danger, color: '#000', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>MC {game.metacritic_score}</span>
              )}
              {game.release_date && <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>{game.release_date}</span>}
            </div>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div style={{ display: 'flex', gap: '8px', padding: '16px 20px', borderBottom: `1px solid ${THEME.border}`, flexWrap: 'wrap', flexShrink: 0 }}>
          <button onClick={handleToggleQueue} disabled={queuing} className="press-effect" style={{ background: isQueued ? THEME.accent : THEME.bgHover, color: isQueued ? '#000' : THEME.text, border: `1px solid ${isQueued ? THEME.accent : THEME.border}`, borderRadius: '10px', padding: '10px 16px', cursor: queuing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', flex: '1', minWidth: '120px' }}>
            {isQueued ? '✓ In Queue' : '+ Add to Queue'}
          </button>
          <button
            onClick={handleHostRemotePlay}
            disabled={hostingRemotePlay}
            className="press-effect"
            style={{ background: '#1a9fff20', color: '#1a9fff', border: '1px solid #1a9fff40', borderRadius: '10px', padding: '10px 14px', cursor: hostingRemotePlay ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
            title="Host Remote Play Together - launches game and notifies squad"
          >
            📡 {hostingRemotePlay ? '...' : 'Host'}
          </button>
          <a href={game.steam_url} target="_blank" rel="noopener noreferrer" className="press-effect" style={{ background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '10px 14px', textDecoration: 'none', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🛒</a>
          <button onClick={() => window.location.href = `steam://nav/games/details/${game.appid}`} className="press-effect" style={{ background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>🎮</button>
        </div>

        {/* Remote Play Instructions Toast */}
        {remotePlayToast && (
          <div style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #1a9fff20, #1a9fff10)', borderBottom: `1px solid #1a9fff30` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <span style={{ fontSize: '18px' }}>📡</span>
              <span style={{ color: '#1a9fff', fontWeight: '600', fontSize: '14px' }}>{remotePlayToast.message}</span>
            </div>
            <p style={{ margin: 0, color: THEME.textMuted, fontSize: '12px' }}>{remotePlayToast.instructions}</p>
          </div>
        )}

        <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
          {/* Description */}
          {game.short_description && <p style={{ color: THEME.textSecondary, fontSize: '13px', lineHeight: '1.7', margin: '0 0 20px' }}>{game.short_description}</p>}

          {/* HowLongToBeat - Compact inline display */}
          {hltbData?.found && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <span style={{ color: THEME.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>⏱️ HLTB:</span>
              {hltbData.main_story && <span style={{ background: `${THEME.accent}20`, color: THEME.accent, padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{hltbData.main_story}h Main</span>}
              {hltbData.main_extra && <span style={{ background: `${THEME.info}20`, color: THEME.info, padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{hltbData.main_extra}h +Extras</span>}
              {hltbData.completionist && <span style={{ background: `${THEME.success}20`, color: THEME.success, padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{hltbData.completionist}h 100%</span>}
            </div>
          )}
          {hltbLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <Spinner size={14} />
              <span style={{ color: THEME.textMuted, fontSize: '12px' }}>Loading HLTB...</span>
            </div>
          )}

          {/* Price Stats Row */}
          {priceData?.lowest_current && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ background: priceData.lowest_current.discount_percent > 0 ? `${THEME.success}15` : THEME.bg, borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${priceData.lowest_current.discount_percent > 0 ? THEME.success : THEME.border}40` }}>
                <span style={{ fontSize: '14px' }}>💰</span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: THEME.text, fontWeight: '700', fontSize: '14px' }}>${priceData.lowest_current.price?.toFixed(2)}</span>
                    {priceData.lowest_current.discount_percent > 0 && (
                      <span style={{ background: THEME.success, color: '#000', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>-{priceData.lowest_current.discount_percent}%</span>
                    )}
                  </div>
                  <div style={{ color: THEME.textMuted, fontSize: '10px' }}>@ {priceData.lowest_current.store}</div>
                </div>
              </div>
            </div>
          )}

          {/* Steam Achievements Section */}
          {achievementsData && !achievementsData.available && achievementsData.reason && (
            <div style={{ background: THEME.bg, borderRadius: '14px', padding: '16px', marginBottom: '24px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>🏆</span>
              <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Achievements unavailable: {achievementsData.reason}</span>
            </div>
          )}
          {achievementsData?.available && achievementsData?.has_achievements === false && (
            <div style={{ background: THEME.bg, borderRadius: '14px', padding: '16px', marginBottom: '24px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>🏆</span>
              <span style={{ color: THEME.textMuted, fontSize: '13px' }}>This game has no Steam achievements</span>
            </div>
          )}
          {achievementsData?.has_achievements && (
            <div style={{ background: THEME.bg, borderRadius: '14px', marginBottom: '24px', border: `1px solid ${THEME.border}`, overflow: 'hidden' }}>
              <button
                onClick={() => setAchievementsExpanded(!achievementsExpanded)}
                style={{ width: '100%', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', cursor: 'pointer', color: THEME.text }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>🏆</span>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Steam Achievements</h3>
                  <span style={{ fontSize: '12px', color: THEME.textMuted }}>
                    {achievementsData.unlocked}/{achievementsData.total} ({achievementsData.completion_percent}%)
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Progress bar */}
                  <div style={{ width: '120px', height: '6px', background: THEME.bgCard, borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${achievementsData.completion_percent}%`, height: '100%', background: `linear-gradient(90deg, ${THEME.success}, ${THEME.supe})`, borderRadius: '3px' }} />
                  </div>
                  <span style={{ fontSize: '16px', transform: achievementsExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
                </div>
              </button>
              {achievementsExpanded && (
                <div style={{ maxHeight: '400px', overflow: 'auto', borderTop: `1px solid ${THEME.border}` }}>
                  {achievementsData.achievements.map((ach, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderBottom: i < achievementsData.achievements.length - 1 ? `1px solid ${THEME.border}` : 'none',
                      opacity: ach.achieved ? 1 : 0.5,
                      background: ach.achieved ? `${THEME.success}08` : 'transparent'
                    }}>
                      <img
                        src={ach.achieved ? ach.icon : ach.icon_gray}
                        alt=""
                        style={{ width: '32px', height: '32px', borderRadius: '4px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: ach.achieved ? THEME.success : THEME.text, fontWeight: '600', fontSize: '13px' }}>
                            {ach.achieved && '✓ '}{ach.display_name}
                          </span>
                          {ach.hidden && !ach.achieved && <span style={{ fontSize: '10px', color: THEME.warning }}>🔒 Hidden</span>}
                        </div>
                        {ach.description && <div style={{ color: THEME.textMuted, fontSize: '11px', marginTop: '2px' }}>{ach.description}</div>}
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '60px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: ach.global_percent < 10 ? THEME.warning : THEME.textMuted }}>
                          {ach.global_percent?.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '9px', color: THEME.textMuted }}>global</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {achievementsLoading && (
            <div style={{ background: THEME.bg, borderRadius: '14px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Spinner size={16} />
              <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Loading achievements...</span>
            </div>
          )}

          {/* IGDB Rich Metadata */}
          {igdbData && (
            <div style={{ background: THEME.bg, borderRadius: '14px', marginBottom: '24px', overflow: 'hidden', border: `1px solid ${THEME.border}` }}>
              <button
                onClick={() => setIgdbExpanded(!igdbExpanded)}
                style={{
                  width: '100%', background: 'transparent', border: 'none', padding: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '18px' }}>📚</span>
                  <span style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>Game Info (IGDB)</span>
                  {igdbData.total_rating && (
                    <span style={{ background: THEME.info, color: '#000', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                      {igdbData.total_rating}/100
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '16px', color: THEME.textMuted, transform: igdbExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
              </button>
              {igdbExpanded && (
                <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${THEME.border}` }}>
                  {/* Summary */}
                  {igdbData.summary && (
                    <p style={{ color: THEME.textMuted, fontSize: '13px', lineHeight: '1.6', margin: '16px 0' }}>{igdbData.summary}</p>
                  )}

                  {/* Ratings Row */}
                  {(igdbData.user_rating || igdbData.critic_rating) && (
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                      {igdbData.user_rating && (
                        <div style={{ flex: 1, background: THEME.bgCard, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.success }}>{igdbData.user_rating}</div>
                          <div style={{ fontSize: '10px', color: THEME.textMuted }}>User Rating ({igdbData.user_rating_count || 0})</div>
                        </div>
                      )}
                      {igdbData.critic_rating && (
                        <div style={{ flex: 1, background: THEME.bgCard, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '20px', fontWeight: '700', color: THEME.info }}>{igdbData.critic_rating}</div>
                          <div style={{ fontSize: '10px', color: THEME.textMuted }}>Critic Rating ({igdbData.critic_rating_count || 0})</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Genres */}
                  {igdbData.genres?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Genres</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {igdbData.genres.map((g, i) => (
                          <span key={i} style={{ background: THEME.bgCard, color: THEME.text, padding: '4px 10px', borderRadius: '6px', fontSize: '11px' }}>{g}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Themes */}
                  {igdbData.themes?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Themes</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {igdbData.themes.map((t, i) => (
                          <span key={i} style={{ background: `${THEME.accent}20`, color: THEME.accent, padding: '4px 10px', borderRadius: '6px', fontSize: '11px' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Game Modes */}
                  {igdbData.game_modes?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Game Modes</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {igdbData.game_modes.map((m, i) => (
                          <span key={i} style={{ background: `${THEME.success}20`, color: THEME.success, padding: '4px 10px', borderRadius: '6px', fontSize: '11px' }}>{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Developers/Publishers */}
                  {(igdbData.developers?.length > 0 || igdbData.publishers?.length > 0) && (
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {igdbData.developers?.length > 0 && (
                        <div>
                          <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Developer</div>
                          <div style={{ color: THEME.text, fontSize: '12px' }}>{igdbData.developers.join(', ')}</div>
                        </div>
                      )}
                      {igdbData.publishers?.length > 0 && (
                        <div>
                          <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>Publisher</div>
                          <div style={{ color: THEME.text, fontSize: '12px' }}>{igdbData.publishers.join(', ')}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Screenshots */}
                  {igdbData.screenshots?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Screenshots</div>
                      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }} className="hide-scrollbar">
                        {igdbData.screenshots.map((ss, i) => (
                          <img key={i} src={ss} alt="" style={{ height: '80px', borderRadius: '8px', flexShrink: 0 }} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Similar Games */}
                  {igdbData.similar_games?.length > 0 && (
                    <div>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Similar Games</div>
                      <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }} className="hide-scrollbar">
                        {igdbData.similar_games.map((sg, i) => (
                          <div key={i} style={{ flexShrink: 0, width: '100px', textAlign: 'center' }}>
                            {sg.cover_url && <img src={sg.cover_url} alt="" style={{ width: '100px', height: '133px', borderRadius: '8px', objectFit: 'cover' }} />}
                            <div style={{ color: THEME.text, fontSize: '11px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sg.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {igdbData.videos?.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ color: THEME.textDim, fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Videos</div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {igdbData.videos.map((v, i) => (
                          <a key={i} href={`https://youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noopener noreferrer"
                            style={{ background: '#ff0000', color: '#fff', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            ▶ {v.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {igdbLoading && (
            <div style={{ background: THEME.bg, borderRadius: '14px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Spinner size={16} />
              <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Loading game info from IGDB...</span>
            </div>
          )}

          {/* Squad Member Ratings - Supports 2-4 members */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            {/* Current user */}
            <div style={{ flex: otherUsers.length > 1 ? '1 1 calc(50% - 6px)' : '1', minWidth: otherUsers.length > 1 ? '140px' : 'auto', background: `${currentUser?.theme_color || THEME.success}10`, borderRadius: '12px', padding: '14px', border: `1px solid ${currentUser?.theme_color || THEME.success}25`, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: currentUser?.theme_color || THEME.success }} />
                <span style={{ color: currentUser?.theme_color || THEME.success, fontWeight: '600', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.display_name || 'You'}</span>
                <span style={{ color: THEME.textMuted, fontSize: '10px', marginLeft: 'auto' }}>{getStatusLabel(myUserData.status)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: otherUsers.length > 1 ? '22px' : '28px', color: currentUser?.theme_color || THEME.success, fontWeight: '700' }}>{myUserData.rating ?? '—'}</span>
                <span style={{ fontSize: '13px', color: THEME.textMuted }}>/10</span>
                <span style={{ fontSize: '11px', color: THEME.textMuted, marginLeft: 'auto' }}>{formatPlaytime(myUserData.playtime)}</span>
              </div>
              {myUserData.notes && <div style={{ color: THEME.textSecondary, fontSize: '11px', fontStyle: 'italic', marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${currentUser?.theme_color || THEME.success}20` }}>"{myUserData.notes}"</div>}
              <button onClick={handleStartEditRating} className="press-effect" style={{ position: 'absolute', top: '10px', right: '10px', background: 'transparent', color: currentUser?.theme_color || THEME.success, border: 'none', fontSize: '14px', cursor: 'pointer', padding: '4px' }} title="Edit rating">✏️</button>
            </div>
            {/* Other squad members - Show ALL */}
            {otherUsers.map(([username, userData]) => (
              <div key={username} style={{ flex: otherUsers.length > 1 ? '1 1 calc(50% - 6px)' : '1', minWidth: otherUsers.length > 1 ? '140px' : 'auto', background: `${userData.color || THEME.info}10`, borderRadius: '12px', padding: '14px', border: `1px solid ${userData.color || THEME.info}25` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: userData.color || THEME.info }} />
                  <span style={{ color: userData.color || THEME.info, fontWeight: '600', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userData.display_name || username}</span>
                  <span style={{ color: THEME.textMuted, fontSize: '10px', marginLeft: 'auto' }}>{getStatusLabel(userData.status)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: otherUsers.length > 1 ? '22px' : '28px', color: userData.color || THEME.info, fontWeight: '700' }}>{userData.rating ?? '—'}</span>
                  <span style={{ fontSize: '13px', color: THEME.textMuted }}>/10</span>
                  <span style={{ fontSize: '11px', color: THEME.textMuted, marginLeft: 'auto' }}>{formatPlaytime(userData.playtime)}</span>
                </div>
                {userData.notes && <div style={{ color: THEME.textSecondary, fontSize: '11px', fontStyle: 'italic', marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${userData.color || THEME.info}20` }}>"{userData.notes}"</div>}
              </div>
            ))}
          </div>

          {/* Genres & Tags - Combined compact section */}
          {(game.genres?.length > 0 || gameTags.length > 0) && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {game.genres?.map((g, i) => <span key={`g-${i}`} style={{ background: THEME.bg, color: THEME.textMuted, padding: '4px 10px', borderRadius: '6px', fontSize: '11px' }}>{g}</span>)}
              {/* Show ALL squad members who recommended */}
              {recommendedByOthers.map(r => (
                <span key={r.username} style={{ background: `${r.color || THEME.success}20`, color: r.color || THEME.success, padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>👍 {r.displayName}</span>
              ))}
            </div>
          )}

          {/* Tags - Inline with add */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {gameTags.length > 0 && (() => {
                const tagsByName = {};
                gameTags.forEach(t => {
                  if (!tagsByName[t.name]) tagsByName[t.name] = [];
                  tagsByName[t.name].push(t);
                });
                return Object.entries(tagsByName).map(([name, tags]) => {
                  const isShared = tags.length > 1;
                  const myTag = tags.find(t => t.user === currentUser?.username);
                  const color = isShared ? null : tags[0].color;
                  return (
                    <span key={name} style={{
                      background: isShared ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : `${color}25`,
                      color: isShared ? '#fff' : color,
                      padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                      #{name}
                      {myTag && <button onClick={() => handleRemoveTag(myTag.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '10px', padding: 0, lineHeight: 1 }}>&times;</button>}
                    </span>
                  );
                });
              })()}
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="text" placeholder="+ tag" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddTag()} list="my-tags" style={{ width: '70px', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '6px', padding: '4px 8px', color: THEME.text, fontSize: '11px', outline: 'none' }} />
                <datalist id="my-tags">{myTags.map(t => <option key={t.id} value={t.name} />)}</datalist>
              </div>
            </div>
          </div>

          {/* Collapsible Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Sessions Section */}
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${THEME.border}` }}>
              <SectionHeader icon="📅" title="Sessions" count={sessions.length} sectionKey="sessions" />
              {sectionsOpen.sessions && (
                <div style={{ background: THEME.bg, borderTop: `1px solid ${THEME.border}` }}>
                  {loading ? <div style={{ padding: '16px' }}><Spinner size={16} /></div> : sessions.length === 0 ? (
                    <div style={{ color: THEME.textDim, fontSize: '12px', padding: '16px', textAlign: 'center' }}>No sessions logged</div>
                  ) : (
                    <div style={{ maxHeight: '150px', overflow: 'auto', padding: '8px' }}>
                      {sessions.map(s => (
                        <div key={s.id} style={{ padding: '8px 10px', borderBottom: `1px solid ${THEME.border}30` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                            <span style={{ color: THEME.text }}>{new Date(s.played_at).toLocaleDateString()}</span>
                            {s.duration_minutes && <span style={{ color: THEME.textMuted }}>{formatPlaytime(s.duration_minutes)}</span>}
                          </div>
                          {s.notes && <div style={{ color: THEME.textMuted, fontSize: '11px', marginTop: '2px' }}>{s.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Chat Section */}
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${THEME.border}` }}>
              <SectionHeader icon="💬" title="Chat" count={messages.length} sectionKey="chat" />
              {sectionsOpen.chat && (
                <div style={{ background: THEME.bg, borderTop: `1px solid ${THEME.border}` }}>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', padding: '10px' }}>
                    {loading ? <Spinner size={16} /> : messages.length === 0 ? (
                      <div style={{ color: THEME.textDim, fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>No messages yet</div>
                    ) : messages.map(m => (
                      <div key={m.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: m.user.color || THEME.textMuted, marginTop: '5px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '1px' }}>
                            <span style={{ color: m.user.color || THEME.text, fontWeight: '600', fontSize: '11px' }}>{m.user.display_name || m.user.username}</span>
                            <span style={{ color: THEME.textDim, fontSize: '9px' }}>{new Date(m.created_at).toLocaleDateString()}</span>
                          </div>
                          <div style={{ color: THEME.text, fontSize: '12px', lineHeight: '1.4' }}>{m.message}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', padding: '10px', borderTop: `1px solid ${THEME.border}`, background: THEME.bgCard }}>
                    <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()} placeholder="Message..." style={{ flex: 1, background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 12px', color: THEME.text, fontSize: '12px', outline: 'none' }} />
                    <button onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()} className="press-effect" style={{ background: THEME.accent, color: '#000', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: (sendingMessage || !newMessage.trim()) ? 'not-allowed' : 'pointer', opacity: (sendingMessage || !newMessage.trim()) ? 0.5 : 1 }}>Send</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      {isEditingRating && <RatingModal game={game} myColor={currentUser?.theme_color || THEME.success} editRating={editRating} setEditRating={setEditRating} editNotes={editNotes} setEditNotes={setEditNotes} saving={savingRating} onSave={handleSaveRating} onClose={() => setIsEditingRating(false)} />}
    </motion.div>
  );
};

export default GameDetailModal;
